import { type ReactElement, useId, useRef, useState } from 'react';
import { Link } from 'react-router';
import { allowedQuoteActions, isQuoteNotFoundError, useMyQuoteDetailQuery } from '@/entities/quote';
import { QuoteActionButtons, RevisionRequestForm } from '@/features/quote-response';
import { MY_QUOTES_PATH } from '@/shared/config';
import { Button, buttonVariants, QuoteDocument, quoteDestinationsForDocument } from '@/shared/ui';
import { QuoteAmountSummary } from './quote-amount-summary';
import { QuoteDesignPreview } from './quote-design-preview';
import { QuoteDetailHeader } from './quote-detail-header';
import { QuotePaymentSummary } from './quote-payment-summary';
import { QuoteProductPreview } from './quote-product-preview';
import { QuoteProgressSteps } from './quote-progress-steps';
import { QuoteRequestSummary } from './quote-request-summary';

export interface MyQuoteDetailProps {
  readonly id: string;
  /** 페이지가 uuid 검증을 통과했는지 — 아니면 요청 없이 못 찾음을 보여 준다. */
  readonly validId: boolean;
}

/**
 * 내 견적 상세(견적 설계 §7-2·§7-3) — 요청 내용·디자인 사본·반려 사유·상태별 버튼·최신 발행본.
 * 상품 상세와 같은 짜임 — 왼쪽 큰 디자인 칸, 오른쪽 요청 내용·확정 금액·버튼, 아래 견적서.
 * 주문된 견적의 상태는 주문 고객 화면 이름이다(주문 설계 §8-4).
 * 견적서(QuoteDocument)만 인쇄되고 나머지는 print:hidden 으로 숨긴다 — 초안은 고객 DTO 에 없다.
 */
export const MyQuoteDetail = ({ id, validId }: MyQuoteDetailProps): ReactElement => {
  const query = useMyQuoteDetailQuery(id, { enabled: validId });
  const [isRevisionOpen, setIsRevisionOpen] = useState(false);
  const revisionButtonId = useId();
  const documentHeadingId = useId();
  const statusRef = useRef<HTMLSpanElement>(null);
  // 받은 상세가 있으면 다시 조회(그림 기다림·창 포커스) 실패는 조용히 넘긴다 — 오류·못 찾음은 아직 받은 것이 없을 때만이다.
  const failedError = query.data === undefined ? query.error : null;
  const notFound = !validId || isQuoteNotFoundError(failedError?.data?.code);

  if (notFound) {
    return (
      <section className="flex flex-col items-center gap-6 border-y border-foreground/10 py-24 text-center">
        <div className="flex flex-col gap-3">
          <h1 className="text-2xl font-bold tracking-[-0.02em] sm:text-3xl">
            견적을 찾을 수 없습니다.
          </h1>
          <p className="text-sm text-muted-foreground">
            삭제됐거나 다른 계정의 견적, 혹은 잘못된 주소일 수 있습니다.
          </p>
        </div>
        <Link to={MY_QUOTES_PATH} className={buttonVariants({ variant: 'outline', size: 'lg' })}>
          내 견적 목록으로
        </Link>
      </section>
    );
  }

  if (query.isPending) {
    return (
      <div>
        <p role="status" className="sr-only">
          견적을 불러오는 중입니다.
        </p>
        <div aria-hidden className="flex flex-col gap-10">
          <div className="h-24 w-full max-w-md animate-pulse bg-muted/70" />
          <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:gap-16">
            <div className="aspect-square w-full animate-pulse bg-muted/70" />
            <div className="min-h-96 animate-pulse bg-muted/70" />
          </div>
        </div>
      </div>
    );
  }

  const detail = query.data;

  /** 닫으면 폼을 연 "수정 요청" 버튼으로 포커스를 돌려준다. */
  const closeRevision = (): void => {
    setIsRevisionOpen(false);
    document.getElementById(revisionButtonId)?.focus();
  };
  /** 보내면 "수정 요청" 버튼이 사라지므로(수정 요청됨) 바뀐 상태 표시로 포커스를 옮긴다. */
  const finishRevision = (): void => {
    setIsRevisionOpen(false);
    statusRef.current?.focus();
  };

  return (
    <>
      {failedError !== null ? (
        <div
          role="alert"
          className="mb-10 flex flex-col items-center gap-4 border-y border-foreground/10 py-12 text-center print:hidden"
        >
          <p className="text-sm text-destructive">견적을 불러오지 못했습니다.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void query.refetch();
            }}
          >
            다시 시도
          </Button>
        </div>
      ) : null}

      {detail !== undefined ? (
        <div className="flex flex-col gap-20 sm:gap-24">
          <div className="flex flex-col gap-10 print:hidden">
            <QuoteDetailHeader detail={detail} statusRef={statusRef} />
            <QuoteProgressSteps status={detail.status} />

            <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:gap-16">
              {detail.design.designBody === null ? (
                <QuoteProductPreview
                  imageUrl={detail.productImageUrl}
                  productName={detail.productName}
                />
              ) : (
                <QuoteDesignPreview
                  quoteRequestId={detail.id}
                  thumbnailUrl={detail.design.thumbnailUrl}
                  designName={detail.design.designName ?? detail.productName}
                  designBody={detail.design.designBody}
                />
              )}

              <div className="flex flex-col gap-8">
                <QuoteRequestSummary detail={detail} />

                {detail.rejectedReason !== null ? (
                  <section
                    aria-label="반려 사유"
                    className="border-l-2 border-destructive bg-destructive/5 px-5 py-4"
                  >
                    <h2 className="text-sm font-semibold text-destructive">반려 사유</h2>
                    <p className="mt-2 text-sm whitespace-pre-wrap">{detail.rejectedReason}</p>
                  </section>
                ) : null}

                <QuoteAmountSummary version={detail.latestIssuedVersion} />

                {detail.payment !== null ? <QuotePaymentSummary payment={detail.payment} /> : null}

                <QuoteActionButtons
                  quoteRequestId={detail.id}
                  status={detail.status}
                  orderId={detail.orderId}
                  onRequestRevision={() => setIsRevisionOpen(true)}
                  revisionButtonId={revisionButtonId}
                />

                {isRevisionOpen &&
                detail.latestIssuedVersion !== null &&
                // 충돌 재조회로 상태가 바뀌면(반려·거절 등) 폼도 함께 닫힌다.
                allowedQuoteActions(detail.status).includes('REQUEST_REVISION') ? (
                  <RevisionRequestForm
                    quoteRequestId={detail.id}
                    versionId={detail.latestIssuedVersion.id}
                    onSubmitted={finishRevision}
                    onCancel={closeRevision}
                  />
                ) : null}
              </div>
            </div>
          </div>

          {detail.latestIssuedVersion !== null ? (
            <section aria-labelledby={documentHeadingId} className="flex flex-col gap-8">
              <h2
                id={documentHeadingId}
                className="border-b border-foreground pb-4 text-2xl leading-tight font-bold tracking-[-0.02em] print:hidden"
              >
                발행된 견적서
              </h2>
              <QuoteDocument
                version={detail.latestIssuedVersion}
                quoteNumber={detail.quoteNumber}
                requestedAt={detail.createdAt}
                destinations={quoteDestinationsForDocument(detail.destinations)}
                className="rounded-none border-foreground/15 sm:p-10"
              />
            </section>
          ) : null}
        </div>
      ) : null}
    </>
  );
};
